import { useState } from "react";
import { Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

export const canDelete = (b) => !b.is_demo && !(b.payment_method === "stripe" && ["active", "past_due"].includes(b.subscription_status));

function ConfirmDialog({ items, onClose, onDone }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const paying = items.filter((b) => b.subscription_status === "active").length;
  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/platform/businesses/bulk-delete", { ids: items.map((b) => b.id), confirm: "DELETE" });
      toast.success(`Deleted ${data.deleted} business${data.deleted === 1 ? "" : "es"}`);
      data.skipped.forEach((s) => toast.warning(`${s.name} kept: ${s.reason}`));
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="bulk-delete-dialog" className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-2xl">Delete {items.length} business{items.length === 1 ? "" : "es"}?</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">This permanently removes each business, its venues, team logins and every record. It can't be undone. Payment history is kept.</p>
        <ul data-testid="bulk-delete-list" className="max-h-40 overflow-y-auto rounded-xl bg-slate-50 p-3 text-sm">{items.map((b) => <li key={b.id} className="truncate">{b.name}<span className="text-slate-500"> · {b.owner_email || "no owner"}</span></li>)}</ul>
        {paying > 0 && <p data-testid="bulk-delete-paying-warning" className="text-sm font-semibold text-amber-700">{paying} of these is marked as active (manual or free-code plan).</p>}
        <form onSubmit={run} className="grid gap-3">
          <label className="text-sm font-medium text-slate-700">Type <b className="font-mono">DELETE</b> to confirm<input data-testid="bulk-delete-confirm-input" autoComplete="off" className="ka-input mt-1.5 font-mono" value={typed} onChange={(e) => setTyped(e.target.value)} /></label>
          <button data-testid="bulk-delete-confirm-button" disabled={typed !== "DELETE" || busy} className="ka-press inline-flex items-center justify-center gap-1.5 rounded-full bg-rose-600 py-2.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}Delete permanently</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function BulkDeleteBar({ selected, list, onClear, onDone }) {
  const [open, setOpen] = useState(false);
  const items = list.filter((b) => selected.has(b.id));
  if (!items.length) return null;
  return (
    <div data-testid="bulk-delete-bar" className="sticky top-2 z-20 mb-3 flex flex-wrap items-center gap-3 rounded-2xl bg-slate-950 px-4 py-3 text-sm text-white shadow-lg">
      <span data-testid="bulk-selected-count" className="font-semibold">{items.length} selected</span>
      <button data-testid="bulk-clear-button" onClick={onClear} className="text-slate-300 hover:text-white">Clear</button>
      <button data-testid="bulk-delete-button" onClick={() => setOpen(true)} className="ka-press ml-auto inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-4 py-2 font-semibold hover:bg-rose-500"><Trash2 className="h-4 w-4" />Delete selected</button>
      {open && <ConfirmDialog items={items} onClose={() => setOpen(false)} onDone={() => { setOpen(false); onDone(); }} />}
    </div>
  );
}
